import { supabase, getCurrentUserId } from "@/lib/supabase";
import { embeddingJobService } from "@/services/embeddingJobService";
import { aiService, type ChatOptions, type StreamingChunk } from "@/services/aiService";
import {
  checkTheWork,
  drawerKey,
  FAST_MODEL,
  HOUSE_RULES_VERSION,
  isDocumentShelfId,
  formatWorkOrderBlock,
  parseBriefJson,
  receiptLine,
  type ShelfBrief,
} from "@shared/operatingLoop";
import {
  prepareOperatingTurn,
  type OperatingContinue,
  type OperatingStore,
  type OperatingTurn,
} from "@shared/operatingLoopRun";
import { tokenSavingsService } from "@/services/tokenSavingsService";
import { shelfLibraryService } from "@/services/shelfLibraryService";
import { authHeaders } from "@/lib/authToken";
import {
  isTokenSavingsSurface,
  stopPathToLever,
  type TokenSavingsSurface,
} from "@shared/tokenSavings";

async function recordLoopStop(input: {
  userId: string;
  path: string;
  surface?: string | null;
  threadId?: string | null;
}): Promise<void> {
  const lever = stopPathToLever(input.path);
  const surface: TokenSavingsSurface = isTokenSavingsSurface(input.surface ?? "")
    ? (input.surface as TokenSavingsSurface)
    : "other";
  const baseline =
    lever === "drawer" || lever === "crisis_stop" || lever === "private_hold"
      ? await tokenSavingsService.averageAiRoomTokens(input.userId)
      : null;
  await tokenSavingsService.recordEvent({
    userId: input.userId,
    surface,
    lever,
    baselineTokens: baseline,
    actualTokens: 0,
    callsAvoided: 1,
    threadId: input.threadId,
  });
}

function privateModelSlug(): string | null {
  const slug = import.meta.env.VITE_OPERATING_LOOP_PRIVATE_MODEL;
  return typeof slug === "string" && slug.trim() ? slug.trim() : null;
}

function rowToBrief(row: Record<string, unknown>): ShelfBrief {
  const sensitivity = row.sensitivity === "client" || row.sensitivity === "restricted" ? row.sensitivity : "general";
  return {
    documentId: String(row.document_id ?? ""),
    whatItIs: String(row.what_it_is ?? ""),
    summary: String(row.summary ?? ""),
    tags: Array.isArray(row.tags) ? row.tags.map(String) : [],
    sensitivity,
  };
}

const browserStore: OperatingStore = {
  async loadBriefs(userId, documentIds) {
    if (documentIds.length === 0) return [];
    const { data, error } = await supabase
      .from("operating_loop_briefs")
      .select("document_id, what_it_is, summary, tags, sensitivity")
      .eq("user_id", userId)
      .in("document_id", documentIds);
    if (error || !data) return [];
    return data.map((row: Record<string, unknown>) => rowToBrief(row));
  },
  async searchGeneralBriefs(userId, question) {
    try {
      const headers = await authHeaders();
      const res = await fetch("/api/embeddings?route=shelf-search", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ query: question, tags: ["general"], limit: 5, agent: "librarian" }),
      });
      if (res.ok) {
        const body = (await res.json()) as { hits?: Array<Record<string, unknown>> };
        const hits = body.hits ?? [];
        if (hits.length) {
          return hits.map((hit) => ({
            documentId: String(hit.sourceId || hit.id || ""),
            whatItIs: String(hit.title || "Shelf"),
            summary: String(hit.content || "").slice(0, 500),
            tags: Array.isArray(hit.shelfTags) ? hit.shelfTags.map(String) : ["general"],
            sensitivity: hit.sensitivity === "client" || hit.sensitivity === "restricted" ? hit.sensitivity : "general",
          }));
        }
      }
    } catch (error) {
      console.warn("[operating-loop] shelf search failed:", error);
    }
    const token = question.trim().split(/\s+/).find((part) => part.length > 4);
    if (!token) return [];
    const { data, error } = await supabase
      .from("operating_loop_briefs")
      .select("document_id, what_it_is, summary, tags, sensitivity")
      .eq("user_id", userId)
      .eq("sensitivity", "general")
      .ilike("summary", `%${token.replace(/[%_]/g, "")}%`)
      .limit(5);
    if (error || !data) return [];
    return data.map((row: Record<string, unknown>) => rowToBrief(row));
  },
  async findDrawerAnswer(userId, key) {
    const { data } = await supabase
      .from("operating_loop_answers")
      .select("answer")
      .eq("user_id", userId)
      .eq("drawer_key", key)
      .maybeSingle();
    return data?.answer ? String(data.answer) : null;
  },
  async insertJobSheet(userId, sheet) {
    const { data } = await supabase
      .from("operating_loop_job_sheets")
      .insert({
        user_id: userId,
        question: sheet.question,
        shelves: sheet.shelves,
        outside_info: sheet.outsideInfo,
        sensitivity: sheet.sensitivity,
        tier: sheet.tier,
        work_order: sheet,
      })
      .select("id")
      .maybeSingle();
    return data?.id ? String(data.id) : null;
  },
  async insertReceipt(input) {
    const { error } = await supabase.from("operating_loop_receipts").insert({
      user_id: input.userId,
      path: input.path,
      model: input.model ?? null,
      total_tokens: input.totalTokens ?? null,
      shelves: input.shelves ?? [],
      job_sheet_id: input.jobSheetId ?? null,
      thread_id: input.threadId ?? null,
    });
    if (error) console.warn("[operating-loop] receipt insert failed:", error.message);
  },
};

function lastUserText(messages: ChatOptions["messages"]): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== "user") continue;
    return typeof message.content === "string" ? message.content : "";
  }
  return "";
}

export const operatingLoopService = {
  async prepareTurn(input: {
    userId: string;
    question: string;
    selectedDocumentIds: string[];
    autoSelectBest: boolean;
    pickedModel: string;
    threadId?: string | null;
  }): Promise<OperatingTurn> {
    const fastMaxChars = await shelfLibraryService.fastMaxChars();
    return prepareOperatingTurn(browserStore, {
      ...input,
      privateModel: privateModelSlug(),
      fastMaxChars,
    });
  },

  async enrichWorkOrder(sheet: import("@shared/operatingLoop").JobSheet) {
    const { validateJobSheet } = await import("@shared/operatingLoop");
    try {
      const raw = await aiService.complete(
        [
          "Return one JSON object with keys goal, key_facts, constraints, acceptance_criteria.",
          "key_facts, constraints, and acceptance_criteria are string arrays.",
          "Do not add any other text.",
          `Request: ${sheet.request}`,
          `Known facts: ${sheet.key_facts.join(" | ")}`,
        ].join("\n"),
        { model: FAST_MODEL, maxTokens: 500, temperature: 0, app: "librarian" },
      );
      const start = raw.indexOf("{");
      const end = raw.lastIndexOf("}");
      if (start < 0 || end <= start) return sheet;
      const parsed = JSON.parse(raw.slice(start, end + 1)) as Record<string, unknown>;
      return validateJobSheet({
        ...sheet,
        goal: typeof parsed.goal === "string" ? parsed.goal : sheet.goal,
        key_facts: Array.isArray(parsed.key_facts) ? parsed.key_facts.map(String) : sheet.key_facts,
        constraints: Array.isArray(parsed.constraints) ? parsed.constraints.map(String) : sheet.constraints,
        acceptance_criteria: Array.isArray(parsed.acceptance_criteria)
          ? parsed.acceptance_criteria.map(String)
          : sheet.acceptance_criteria,
      });
    } catch (error) {
      await shelfLibraryService.logCall({
        agent: "librarian",
        model: FAST_MODEL,
        status: "schema_reject",
      });
      console.warn("[operating-loop] librarian JSON rejected:", error);
      return sheet;
    }
  },

  async gateContinue(turn: OperatingTurn): Promise<OperatingTurn> {
    if (turn.path !== "continue") return turn;
    if (await shelfLibraryService.overMonthlyBudget()) {
      return {
        path: "invalid_sheet",
        message: "This project is over its monthly model budget, so the request was not sent.",
        receiptLine: "Stopped · budget · no model",
      };
    }
    const gate = await shelfLibraryService.requestSlackGate(turn.workOrder);
    if (!gate.approved) {
      return { path: "invalid_sheet", message: gate.message, receiptLine: "Stopped · awaiting Slack · no model" };
    }
    const workOrder = await this.enrichWorkOrder(turn.workOrder);
    return { ...turn, workOrder, briefPrompt: formatWorkOrderBlock(workOrder) };
  },

  async rememberAnswer(input: {
    userId: string;
    question: string;
    answer: string;
    shelfIds: string[];
    model: string | null;
  }): Promise<void> {
    const answer = input.answer.trim();
    if (!answer) return;
    const { error } = await supabase.from("operating_loop_answers").upsert(
      {
        user_id: input.userId,
        drawer_key: drawerKey(input.question, input.shelfIds),
        question: input.question.trim(),
        answer,
        shelf_ids: input.shelfIds,
        house_rules_version: HOUSE_RULES_VERSION,
        model: input.model,
      },
      { onConflict: "user_id,drawer_key" },
    );
    if (error) console.warn("[operating-loop] drawer upsert failed:", error.message);
  },

  saveReceipt: browserStore.insertReceipt,

  async settleAnswer(input: {
    userId: string;
    turn: OperatingContinue;
    answer: string;
    model: string;
    totalTokens: number | null;
    threadId?: string | null;
    app?: string;
  }): Promise<{ answer: string; receiptLine: string }> {
    let answer = input.answer.trim();
    let check = checkTheWork({
      answer,
      question: input.turn.question,
      sensitivity: input.turn.sensitivity,
      clientExcerpts: input.turn.clientExcerpts,
    });
    if (!check.ok && input.turn.tier === "fast") {
      try {
        const retry = await aiService.complete(
          `Answer the question directly.\nQuestion: ${input.turn.question}\nDraft:\n${answer}`,
          { model: input.model, maxTokens: 800, temperature: 0.2, app: input.app ?? "other" },
        );
        if (retry.trim()) {
          answer = retry.trim();
          check = checkTheWork({
            answer,
            question: input.turn.question,
            sensitivity: input.turn.sensitivity,
            clientExcerpts: input.turn.clientExcerpts,
          });
        }
      } catch (retryErr) {
        console.warn("[operating-loop] stronger retry skipped:", retryErr);
      }
    }
    let line = receiptLine("ai_room", input.model, input.totalTokens);
    if (!check.ok && check.reason) line = `${line} · check: ${check.reason}`;
    await this.rememberAnswer({
      userId: input.userId,
      question: input.turn.question,
      answer,
      shelfIds: input.turn.drawerShelfIds,
      model: input.model,
    });
    await shelfLibraryService.writeOutcome(input.turn.workOrder, answer);
    await this.saveReceipt({
      userId: input.userId,
      path: "ai_room",
      model: input.model,
      totalTokens: input.totalTokens,
      shelves: input.turn.shelves,
      jobSheetId: input.turn.jobSheetId,
      threadId: input.threadId,
    });
    return { answer, receiptLine: line };
  },

  async writeBrief(input: { userId: string; documentId: string; title: string; content: string }): Promise<void> {
    const source = input.content.replace(/\s+/g, " ").trim().slice(0, 6000);
    if (!source) return;
    const raw = await aiService.complete(
      [
        "Write a library brief as JSON with keys whatItIs, summary, tags, sensitivity.",
        "sensitivity is general, client, or restricted. Use client or restricted only when the text is explicitly marked that way.",
        "summary is at most 80 words.",
        `Title: ${input.title}`,
        source,
      ].join("\n"),
      { model: FAST_MODEL, maxTokens: 400, temperature: 0, app: "other" },
    );
    const parsed = parseBriefJson(raw);
    if (!parsed) return;
    const { error } = await supabase.from("operating_loop_briefs").upsert(
      {
        user_id: input.userId,
        document_id: input.documentId,
        what_it_is: parsed.whatItIs,
        summary: parsed.summary,
        tags: parsed.tags,
        sensitivity: parsed.sensitivity,
        house_rules_version: HOUSE_RULES_VERSION,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id,document_id" },
    );
    if (error) {
      console.warn("[operating-loop] brief upsert failed:", error.message);
      return;
    }
    if (parsed.sensitivity === "general" && isDocumentShelfId(input.documentId)) {
      await embeddingJobService.enqueue([
        { job_type: "document", target_id: input.documentId, payload: { source: "operating-loop-brief" } },
      ]).catch((err) => console.warn("[operating-loop] brief embed enqueue failed:", err));
    }
  },

  async answerWithLoop(input: {
    userId: string | null | undefined;
    question: string;
    pickedModel: string;
    autoSelectBest?: boolean;
    selectedDocumentIds?: string[];
    threadId?: string | null;
    app?: string;
    run: (ctx: { model: string; briefPrompt: string; outsideInfo: boolean }) => Promise<{ text: string; totalTokens?: number | null }>;
  }): Promise<{ text: string; stopped: boolean; receiptLine?: string }> {
    if (!input.userId || !input.question.trim()) {
      const plain = await input.run({ model: input.pickedModel, briefPrompt: "", outsideInfo: true });
      return { text: plain.text, stopped: false };
    }
    let turn: OperatingTurn | null = null;
    try {
      turn = await this.prepareTurn({
        userId: input.userId,
        question: input.question,
        selectedDocumentIds: input.selectedDocumentIds ?? [],
        autoSelectBest: Boolean(input.autoSelectBest),
        pickedModel: input.pickedModel,
        threadId: input.threadId,
      });
    } catch (error) {
      console.warn("[operating-loop] answer fell through:", error);
      const plain = await input.run({ model: input.pickedModel, briefPrompt: "", outsideInfo: true });
      return { text: plain.text, stopped: false };
    }
    if (turn.path !== "continue") {
      await recordLoopStop({ userId: input.userId, path: turn.path, surface: input.app, threadId: input.threadId });
      return { text: turn.message, stopped: true, receiptLine: turn.receiptLine };
    }
    if (await shelfLibraryService.overMonthlyBudget()) {
      return {
        text: "This project is over its monthly model budget, so the request was not sent.",
        stopped: true,
        receiptLine: "Stopped · budget · no model",
      };
    }
    const gate = await shelfLibraryService.requestSlackGate(turn.workOrder);
    if (!gate.approved) {
      return { text: gate.message, stopped: true, receiptLine: "Stopped · awaiting Slack · no model" };
    }
    let workOrder = turn.workOrder;
    try {
      const enriched = await this.enrichWorkOrder(workOrder);
      workOrder = enriched;
    } catch (error) {
      console.warn("[operating-loop] work order enrich failed:", error);
    }
    const briefPrompt = formatWorkOrderBlock(workOrder);
    const model = turn.overrideModel ? turn.model : input.pickedModel;
    const ran = await input.run({ model, briefPrompt, outsideInfo: turn.outsideInfo });
    const settled = await this.settleAnswer({
      userId: input.userId,
      turn,
      answer: ran.text,
      model,
      totalTokens: ran.totalTokens ?? null,
      threadId: input.threadId,
      app: input.app,
    });
    return { text: settled.answer, stopped: false, receiptLine: settled.receiptLine };
  },

  async *streamGuarded(options: ChatOptions): AsyncGenerator<StreamingChunk, void, unknown> {
    const userId = await getCurrentUserId();
    const question = lastUserText(options.messages);
    if (!userId || !question.trim()) {
      yield* aiService.streamChat(options);
      return;
    }
    let turn: OperatingTurn | null = null;
    try {
      turn = await this.prepareTurn({
        userId,
        question,
        selectedDocumentIds: [],
        autoSelectBest: false,
        pickedModel: options.model,
      });
    } catch (error) {
      console.warn("[operating-loop] plugin stream fell through:", error);
      yield* aiService.streamChat(options);
      return;
    }
    if (turn.path !== "continue") {
      await recordLoopStop({ userId, path: turn.path, surface: options.app ?? "agents" });
      yield { type: "delta", content: turn.message };
      yield { type: "done" };
      return;
    }
    const briefPrompt = turn.briefPrompt;
    const messages = options.messages.some((message) => message.role === "system")
      ? options.messages.map((message) =>
          message.role === "system"
            ? { ...message, content: `${briefPrompt}\n\n${typeof message.content === "string" ? message.content : ""}` }
            : message,
        )
      : [{ role: "system" as const, content: briefPrompt }, ...options.messages];
    const model = turn.overrideModel ? turn.model : options.model;
    let text = "";
    let total: number | null = null;
    for await (const chunk of aiService.streamChat({
      ...options,
      model,
      messages,
      webSearchEnabled: Boolean(options.webSearchEnabled) && turn.outsideInfo,
    })) {
      if (chunk.type === "delta" && chunk.content) text += chunk.content;
      if (chunk.type === "done" && chunk.usage) {
        const prompt = chunk.usage.prompt_tokens ?? 0;
        const completion = chunk.usage.completion_tokens ?? 0;
        if (prompt || completion) total = prompt + completion;
      }
      yield chunk;
    }
    await this.settleAnswer({
      userId,
      turn,
      answer: text,
      model,
      totalTokens: total,
      app: options.app ?? "agents",
    }).catch((error) =>
      console.warn("[operating-loop] plugin settle failed:", error),
    );
  },
};

export function scheduleOperatingBrief(input: { userId: string; documentId: string; title: string; content: string }): void {
  const content = input.content.replace(/\s+/g, " ").trim();
  if (!input.userId || !input.documentId || !content) return;
  void operatingLoopService.writeBrief({ ...input, content }).catch((error) =>
    console.warn("[operating-loop] brief failed:", error),
  );
}
